import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import PageWrapper from 'templates/PageWrapper/PageWrapper';
import PdfDocumentPreviewModal from 'molecules/PdfDocumentPreviewModal';
import { useTenant } from 'tenants/TenantProvider';
import { helpCenterDocuments } from './helpCenterDocuments';

const hexToRgba = (hexColor: string, alpha: number) => {
  const normalized = hexColor.replace('#', '');

  if (normalized.length !== 6) {
    return hexColor;
  }

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

type ActiveDocumentState = {
  fileName: string;
  pdfSource: string | null;
  title: string;
};

const HelpCenterPage = () => {
  const { branding } = useTenant();
  const [activeDocument, setActiveDocument] =
    useState<ActiveDocumentState | null>(null);
  const primaryColor = branding.colors.borderAccent || branding.colors.primary;
  const buttonColor = branding.colors.button || branding.colors.primary;
  const heroBackground = hexToRgba(primaryColor, 0.12);

  const documentCountLabel = useMemo(
    () => `${helpCenterDocuments.length} PDF documents`,
    []
  );

  const openDocument = async (documentId: string) => {
    const selectedDocument = helpCenterDocuments.find(
      (item) => item.id === documentId
    );

    if (!selectedDocument) {
      return;
    }

    setActiveDocument({
      fileName: selectedDocument.fileName,
      pdfSource: null,
      title: selectedDocument.title,
    });

    const pdfSource = await selectedDocument.buildPdf();

    setActiveDocument({
      fileName: selectedDocument.fileName,
      pdfSource,
      title: selectedDocument.title,
    });
  };

  return (
    <PageWrapper title="Help Center">
      <>
        <Stack spacing={3} pb={4}>
          <Box
            sx={{
              borderRadius: '20px',
              p: { xs: 2.5, md: 3.5 },
              background: heroBackground,
              border: `1px solid ${hexToRgba(primaryColor, 0.28)}`,
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-80px',
                right: '-20px',
                width: '220px',
                height: '220px',
                borderRadius: '999px',
                background: hexToRgba(primaryColor, 0.1),
              },
            }}
          >
            <Stack spacing={1.75} sx={{ position: 'relative', zIndex: 1 }}>
              <Chip
                label={branding.appName}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  color: primaryColor,
                  fontWeight: 700,
                  backgroundColor: hexToRgba(primaryColor, 0.12),
                  border: `1px solid ${hexToRgba(primaryColor, 0.22)}`,
                }}
              />
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography
                  variant="h4"
                  color="text.primary"
                  sx={{ maxWidth: '760px' }}
                >
                  Review support documents in one place
                </Typography>
                <Chip
                  label={documentCountLabel}
                  size="small"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 700,
                    backgroundColor: primaryColor,
                  }}
                />
              </Stack>
              <Typography
                color="#575D66"
                sx={{ maxWidth: '760px', fontSize: '1rem' }}
              >
                Open any document in a preview modal, review the PDF, and
                download a copy when you need to share or archive it.
              </Typography>
              <Button
                variant="outlined"
                component="a"
                href={`mailto:${branding.supportEmail}`}
                sx={{
                  alignSelf: 'flex-start',
                  borderColor: primaryColor,
                  color: primaryColor,
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: primaryColor,
                    backgroundColor: hexToRgba(primaryColor, 0.08),
                  },
                }}
              >
                Contact us
              </Button>
            </Stack>
          </Box>

          <Stack spacing={2}>
            {helpCenterDocuments.map((document) => (
              <Card
                key={document.id}
                elevation={0}
                sx={{
                  border: `1px solid ${hexToRgba(primaryColor, 0.18)}`,
                  borderRadius: '18px',
                  overflow: 'hidden',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: '0 auto 0 0',
                    width: '6px',
                    backgroundColor: primaryColor,
                  },
                }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ maxWidth: '780px' }}>
                      <Chip
                        label="PDF document"
                        size="small"
                        sx={{
                          mb: 1.25,
                          color: '#575D66',
                          fontWeight: 700,
                          backgroundColor: '#F2F5F7',
                        }}
                      />
                      <Typography
                        variant="h6"
                        color="text.primary"
                        gutterBottom
                        sx={{ fontWeight: 700 }}
                      >
                        {document.title}
                      </Typography>
                      <Typography sx={{ color: '#575D66' }}>
                        {document.summary}
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => openDocument(document.id)}
                      sx={{
                        minWidth: 160,
                        alignSelf: { xs: 'stretch', md: 'center' },
                        backgroundColor: buttonColor,
                        fontWeight: 700,
                        '&:hover': {
                          backgroundColor: buttonColor,
                        },
                      }}
                    >
                      Review PDF
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Stack>

        {activeDocument ? (
          <PdfDocumentPreviewModal
            fileName={activeDocument.fileName}
            onClose={() => setActiveDocument(null)}
            pdfSource={activeDocument.pdfSource}
            title={activeDocument.title}
          />
        ) : (
          <></>
        )}
      </>
    </PageWrapper>
  );
};

export default HelpCenterPage;
